/**
 * Symbolic inversion of a formula: given `y = f(x)`, produces `x = f⁻¹(y)` — the engine behind
 * bidirectional formulas (docs/Grid/FORMULAS.md, "Reserved for later stages": editing
 * `[A]*1.21 = [B]` on either side computes the other).
 *
 * Only possible when the target variable appears EXACTLY ONCE on each path (once per IF/IFS
 * branch, never in a condition) and every operator on the path from the root down to that
 * reference is invertible. Anywhere else (`[A]^2`,
 * `[A]` appearing twice, an exponent that itself contains the target) there is no unique
 * solution, and this returns a reason instead of guessing.
 *
 * Deliberately does not special-case domain issues (an even root of what turns out to be
 * negative, division by what turns out to be zero): the derived formula is evaluated by the
 * ordinary `formulaLanguage.ts` evaluator like any other, which already reports `#¡NUM!` /
 * `#¡DIV/0!` for those at runtime — this module only performs the algebraic rearrangement.
 */

import { isComparison, type AstNode, type BinaryOperator } from './language'

export interface InverseFailure {
  ok: false
  reason: string
}

export interface InverseSuccess {
  ok: true
  ast: AstNode
}

export type InverseResult = InverseSuccess | InverseFailure

/** Zero-width span for a synthetic node: inversion builds new nodes that never existed in the
 *  user's source text, so there is no real range to point an editor error at. */
const SYNTH = { start: 0, end: 0 }

const numberNode = (n: number): AstNode => ({ kind: 'number', n, text: String(n), ...SYNTH })
const refNode = (field: string): AstNode => ({ kind: 'ref', field, ...SYNTH })
const binaryNode = (op: BinaryOperator, left: AstNode, right: AstNode): AstNode => ({ kind: 'binary', op, left, right, ...SYNTH })

/** Binding power, only as much of it as parenthesising needs: a mirror of the parser's own
 *  table, which is private to `language.ts` because nothing else had a reason to rank operators. */
const PRECEDENCE: Record<BinaryOperator, number> = { '=': 0, '<>': 0, '<': 0, '>': 0, '<=': 0, '>=': 0, '&': 1, '+': 2, '-': 2, '*': 3, '/': 3, '^': 4 }

/**
 * Parenthesises an assembled inverse before it becomes the operand of another operator.
 *
 * `group` is transparent to the evaluator, so this changes no result; it is the SERIALIZER that
 * needs it. Nothing adds parentheses when printing — a `group` node IS the parenthesis the user
 * wrote — so without this `(y-b)/c` would be written out as `y-b/c`, which means something else
 * to anyone who reads it or re-parses it.
 *
 * Only where the grammar would otherwise regroup the expression: a tighter operator around a
 * looser one, or an equally tight one on the right of a non-associative `-`/`/`. A redundant
 * parenthesis is not wrong, but it is noise in a formula the user is expected to read.
 */
const operand = (node: AstNode, op: BinaryOperator, side: 'left' | 'right'): AstNode => {
  const regrouped = node.kind === 'binary'
    ? PRECEDENCE[op] > PRECEDENCE[node.op] || (side === 'right' && PRECEDENCE[op] === PRECEDENCE[node.op])
    // Unary binds tighter than every binary operator, so it only needs protecting from the
    // operator on its own left: `2-(-y)` written as `2--y` is a different piece of text.
    : node.kind === 'unary' && side === 'right'
  return regrouped ? { kind: 'group', arg: node, ...SYNTH } : node
}

/** `-x`: unary binds tighter than anything binary, so any binary operand needs a parenthesis. */
const unaryOperand = (node: AstNode): AstNode =>
  node.kind === 'binary' ? { kind: 'group', arg: node, ...SYNTH } : node

function countRefs(node: AstNode, field: string): number {
  switch (node.kind) {
    case 'ref':
      return node.field === field ? 1 : 0
    case 'range':
      return countRefs(node.from, field) + countRefs(node.to, field)
    case 'number':
    case 'text':
    // Unparsed text is opaque: it may mention the field, but not in a form anything can invert.
    case 'raw':
    case 'name':
      return 0
    case 'call':
      return node.args.reduce((total, arg) => total + countRefs(arg, field), 0)
    case 'unary':
    case 'percent':
    case 'group':
      return countRefs(node.arg, field)
    case 'binary':
      return countRefs(node.left, field) + countRefs(node.right, field)
  }
}

const containsRef = (node: AstNode, field: string): boolean => countRefs(node, field) > 0

/**
 * `ast` with every `LET` replaced by its body and every bound name by the expression it names,
 * so the inversion below only ever sees plain references. An operator value is wrapped in a
 * `group`, which the evaluator ignores, so that serializing the result keeps its precedence:
 * `LET(x; a+b; x*2)` inlines to `(a+b)*2`, not `a+b*2`.
 *
 * Names are matched case-insensitively and scoped exactly as the evaluator scopes them: each
 * binding sees the ones before it, and an inner LET shadows an outer one. A name bound nowhere
 * is left as it is and still evaluates to `#¡NOMBRE?`.
 */
export function inlineLets(node: AstNode, env: ReadonlyMap<string, AstNode> = new Map()): AstNode {
  switch (node.kind) {
    case 'name':
      return env.get(node.name.toLocaleUpperCase('es')) ?? node
    case 'number':
    case 'text':
    case 'raw':
    case 'ref':
    case 'range':
      return node
    case 'call': {
      if (node.name !== 'LET') return { ...node, args: node.args.map((arg) => inlineLets(arg, env)) }
      const scope = new Map(env)
      for (let i = 0; i + 1 < node.args.length; i += 2) {
        const nameNode = node.args[i]!
        if (nameNode.kind !== 'name') return node
        const value = inlineLets(node.args[i + 1]!, scope)
        scope.set(nameNode.name.toLocaleUpperCase('es'), value.kind === 'binary' || value.kind === 'unary' ? { kind: 'group', arg: value, ...SYNTH } : value)
      }
      const body = node.args[node.args.length - 1]
      return body === undefined ? node : inlineLets(body, scope)
    }
    case 'unary':
    case 'percent':
    case 'group':
      return { ...node, arg: inlineLets(node.arg, env) }
    case 'binary':
      return { ...node, left: inlineLets(node.left, env), right: inlineLets(node.right, env) }
  }
}

/** The argument positions of a conditional call that are CONDITIONS rather than results:
 *  `IF(c; a; b)` has one, `IFS(c1; v1; c2; v2)` every even one. Empty for any other call. */
export const conditionIndexes = (node: Extract<AstNode, { kind: 'call' }>): number[] => {
  if (node.name === 'IF') return [0]
  if (node.name === 'IFS') return node.args.flatMap((_, i) => (i % 2 === 0 ? [i] : []))
  return []
}

/**
 * Stands in for the inverse of a branch that does not contain the target: no value of the
 * target makes that branch produce anything else. `IFS(0; 0)` evaluates to `#N/A`, which is what
 * a row taking this branch gets back — this language has no NA() to say it directly.
 */
const noSolution = (): AstNode => ({ kind: 'call', name: 'IFS', args: [numberNode(0), numberNode(0)], ...SYNTH })

/**
 * `ast` is the formula for `resultField` (i.e. `resultField = ast`). Returns the formula that
 * computes `targetField` from `resultField` and everything else `ast` referenced, or a reason
 * it cannot be built.
 *
 * `LET` is inlined first. `IF`/`IFS` invert branch by branch into the same conditional —
 * `y = IF(c; a*2; a+1)` gives `a = IF(c; y/2; y-1)` — so the row picks its branch when the
 * inverse is evaluated, exactly as it does when the formula is. A branch without the target
 * becomes `#N/A`; a condition WITH the target is refused, since the branch would then depend on
 * the very value being solved for (`goalSeek.ts` freezes those conditions to the row's current
 * value before calling this).
 */
export function invertFormula(ast: AstNode, targetField: string, resultField: string): InverseResult {
  const root = inlineLets(ast)
  if (!containsRef(root, targetField)) return { ok: false, reason: `«${targetField}» no aparece en la fórmula.` }

  // `current` is the inverse assembled so far, peeling operators off `node` from the outside in;
  // `node` narrows toward the single reference to `targetField` on this path.
  const invert = (node: AstNode, current: AstNode): InverseResult => {
    switch (node.kind) {
      case 'ref':
        // Reached the target itself: `current` is now the fully assembled inverse.
        return { ok: true, ast: current }

      case 'number':
      case 'text':
      case 'raw':
      case 'name':
        // Unreachable: only a subtree that contains the target reference is ever descended into.
        return { ok: false, reason: 'No se puede despejar: la fórmula no lleva a la columna buscada.' }

      case 'range':
        return { ok: false, reason: 'No se puede despejar un rango.' }

      case 'call': {
        const conditions = conditionIndexes(node)
        if (conditions.length === 0) {
          // Inverting a function needs its own inverse (CHAR has none that is numeric). Reported
          // explicitly instead of guessing.
          return { ok: false, reason: `No se puede despejar dentro de «${node.name}()».` }
        }
        if (conditions.some((i) => containsRef(node.args[i]!, targetField))) {
          return { ok: false, reason: `«${targetField}» aparece en la condición de «${node.name}()».` }
        }
        const args: AstNode[] = []
        // IF without `si_falso` returns 0 there: a constant, so a branch with no solution.
        const count = node.name === 'IF' ? 3 : node.args.length
        for (let i = 0; i < count; i++) {
          const arg = node.args[i]
          if (conditions.includes(i)) {
            args.push(arg!)
          } else if (arg !== undefined && containsRef(arg, targetField)) {
            const branch = invert(arg, current)
            if (!branch.ok) return branch
            args.push(branch.ast)
          } else {
            args.push(noSolution())
          }
        }
        return { ok: true, ast: { kind: 'call', name: node.name, args, ...SYNTH } }
      }

      case 'group':
        return invert(node.arg, current)

      case 'unary':
        // y = -a  =>  a = -y.  y = +a  =>  a = y (no-op).
        return invert(node.arg, node.op === '-' ? { kind: 'unary', op: '-', arg: unaryOperand(current), ...SYNTH } : current)

      case 'percent':
        // y = a%  =  a/100  =>  a = y*100
        return invert(node.arg, binaryNode('*', operand(current, '*', 'left'), numberNode(100)))

      case 'binary': {
        const targetOnLeft = containsRef(node.left, targetField)
        const other = targetOnLeft ? node.right : node.left
        // Both operands holding the target is how it appears twice on one path. Across the
        // branches of an IF it is fine: only one of them is ever taken.
        if (containsRef(other, targetField)) {
          return { ok: false, reason: `«${targetField}» aparece más de una vez: no hay una única forma de despejarlo.` }
        }

        if (isComparison(node.op)) {
          return { ok: false, reason: 'No se puede despejar una comparación.' }
        }

        if (node.op === '&') {
          // `y = a&b` has no unique numeric solution for `a`: text concatenation is not
          // invertible without knowing where to cut.
          return { ok: false, reason: 'No se puede despejar una concatenación de texto.' }
        }

        if (node.op === '^') {
          // y = a^b: only invertible when the target is the BASE and the exponent doesn't
          // depend on it (already guaranteed) — `a = y^(1/b)`. An exponent-side target (`y = a^x`)
          // would need a logarithm, which this language does not have; that failure is reported
          // explicitly rather than silently producing a wrong formula.
          if (!targetOnLeft) return { ok: false, reason: 'No se puede despejar un exponente con las funciones disponibles.' }
          const reciprocal: AstNode = { kind: 'group', arg: binaryNode('/', numberNode(1), other), ...SYNTH }
          return invert(node.left, binaryNode('^', operand(current, '^', 'left'), reciprocal))
        }

        return invert(targetOnLeft ? node.left : node.right, invertBinary(node.op, current, other, targetOnLeft))
      }
    }
  }

  return invert(root, refNode(resultField))
}

/** The non-`^` cases: standard algebraic rearrangement, left or right depending on which side
 *  held the target. */
function invertBinary(op: BinaryOperator, assembled: AstNode, other: AstNode, targetOnLeft: boolean): AstNode {
  /** The assembled inverse so far, parenthesised only if the new operator would regroup it. */
  const current = (newOp: BinaryOperator, side: 'left' | 'right'): AstNode => operand(assembled, newOp, side)
  switch (op) {
    case '+':
      // y = a+b  =>  a = y-b  (symmetric: same either side)
      return binaryNode('-', current('-', 'left'), other)
    case '-':
      // y = a-b  =>  a = y+b            (target on the left)
      // y = a-b  =>  b = a-y            (target on the right)
      return targetOnLeft
        ? binaryNode('+', current('+', 'left'), other)
        : binaryNode('-', other, current('-', 'right'))
    case '*':
      // y = a*b  =>  a = y/b  (symmetric)
      return binaryNode('/', current('/', 'left'), other)
    case '/':
      // y = a/b  =>  a = y*b            (target on the left)
      // y = a/b  =>  b = a/y            (target on the right)
      return targetOnLeft
        ? binaryNode('*', current('*', 'left'), other)
        : binaryNode('/', other, current('/', 'right'))
    case '^':
    case '&':
    case '=':
    case '<>':
    case '<':
    case '>':
    case '<=':
    case '>=':
      // All handled by the caller before reaching here.
      throw new Error('unreachable: ^ and & are handled separately')
  }
}
